/* ===== world/ruin-site.js — 유적 자리 · 유적 짓기 · 봉인실 · 제단 ===== */
import { factory as Factory } from '../ctx.js';
import { clamp } from '../../engine/core/math.js';
import { mixin } from '../../engine/core/mixin.js';
import { RNG } from '../../engine/core/rng.js';
import { weightedKey } from '../../engine/procgen/cells.js';
import { tr } from '../lang.js';
import { SHIFT } from '../size.js';
import { T, TILE_DEF } from '../data.js';
import { MYSTIC_W, RUIN_CIPHER, RUIN_HINTS, RUIN_MAP_IN, RUIN_RELIC, STORY_RUIN } from '../data/ruins.js';
import { TS, World } from '../world.js';
/* world.js 의 World 에서 나눈 조각 — 읽히는 순간 World.prototype 에 붙는다(main.js 가 world.js 다음에 읽는다). */

export const WorldRuinSite: Bag & ThisType<World> = {

  buildRuinSite(spec: Bag, idx: number, rng: RNG) {
    const y0 = spec.y, x0 = spec.x - (spec.w >> 1);
    /* 유적마다 자르는 깊이와 방 최소 크기를 달리 준다 (RUIN_SPEC[].bsp). */
    const bsp = spec.maze ? [7, 10, 8] : (spec.bsp || [4, 15, 8]);   // 미로도 방 속이 8×6 은 되게
    const rooms = this.carveDungeon({
      x0, y0, w: spec.w, h: spec.h, wall: spec.wall, floor: spec.floor, bg: spec.bg,
      rng, depth: bsp[0], minW: bsp[1], minH: bsp[2],
      target: spec.rooms,                                    // 등급대로 방 수를 맞춘다
      plan: spec.plan,                                       // 겉모양이 방 배치를 따라간다
      worn: 1, maze: spec.maze,                              // 미로는 잔방이 정체성이라 큰 홀을 안 뗀다
      style: spec.style, shapes: spec.shapes                 // 유적마다 구조 경향 · 방 모양
    });
    rooms.sort((a: any, b: any) => (b.grand | 0) - (a.grand | 0) || (b.w * b.h) - (a.w * a.h));   // 큰 홀이 주인 방
    const boss = rooms[0];                                   // 가장 넓은 방이 보스방
    const site: Bag = { id: spec.id, n: spec.n, x: spec.x, y: y0 + (spec.h >> 1), w: spec.w, h: spec.h, rooms, idx };

    this._ruinCtx = { x0, y0, w: spec.w, rooms };             // 입구가 옆문을 낼 방을 고른다
    const ex = this.carveRuinEntrance(spec, x0, y0, rng);
    this._ruinCtx = null;
    if (spec.plan === 'tri') this._finishPyramid(spec, x0, y0);
    // 입구 통로의 작은 방 자리 — 진단이 "이 길에 함정이 있나"를 재는 데 쓴다
    site.ent = (this._entranceRooms || []).slice();

    /* 방 성격을 배분한다. */
    const entX = ex === null ? spec.x : ex;
    const rest = rooms.slice(1);
    rest.sort((a: any, b: any) => Math.abs(b.x - entX) - Math.abs(a.x - entX));   // 먼 것부터
    const roles = new Map();
    if (rest[0]) roles.set(rest[0], 'vault');
    if (rest[1]) roles.set(rest[1], 'vault');
    if (rest[Math.floor(rest.length / 2)]) roles.set(rest[Math.floor(rest.length / 2)], 'lore');
    for (const r of rest) if (!roles.has(r) && rng.chance(0.3)) roles.set(r, 'gauntlet');

    /* 고유 방 — 그 유적에만 있는 방 하나. */
    const sigRoom = rest.find((r: any) => !roles.has(r)) || rest[2] || rest[0];
    if (spec.sig && sigRoom) roles.set(sigRoom, 'sig');
    /* 암호 골방은 자물쇠가 걸린 유적이면 어디든 선다(RUIN_CIPHER). */
    let cipherRoom = null;
    if (RUIN_CIPHER[spec.id]) {
      cipherRoom = (spec.sig === 'sunshaft' && sigRoom) ? sigRoom
        : (rest.find((r: any) => !roles.has(r) && r.w >= 14) || rest.find((r: any) => r.w >= 14) || sigRoom);
    }
    /* 신비한 방 — 한 세계에 두세 곳뿐이라 유적마다 후보 하나만 두고, buildRuins 가 미리 뽑아 둔 목록(this._mysticPick)에 든 유적에만 실제로 짓는다. */
    if (spec.mystic) {
      const mr = rest.find((r: any) => !roles.has(r) && r.w >= 12);
      if (mr) roles.set(mr, 'mystic');
    }
    /* 미로 유적만 — 방과 방 사이 통로 몇 개를 도로 막아 **막다른 길**을 만든다. */
    if (spec.maze) {
      for (const r of rooms) {
        if (r === boss || !rng.chance(0.55)) continue;
        const side = rng.chance(0.5) ? r.x : r.x + r.w - 1;
        for (let y = r.y + 1; y < r.y + r.h - 1; y++)
          if (this.get(side, y) === T.AIR) this.set(side, y, spec.wall);
      }
      // 가짜 문 — 벽 한 칸만 유적 타일로 바꿔 두면 통로처럼 보이다 막힌다
      for (const r of rooms) {
        if (rng.chance(0.4)) this.setWall(r.x + (r.w >> 1), r.y + r.h - 2, spec.bg);
      }
    }

    let hintSlot = 0;                                        // 흔적을 방마다 하나씩 순서대로
    // 유적마다 개별로 매긴 난이도 — 없으면 예전 기본값으로 떨어진다
    const TRAP = spec.trapRate === undefined ? 0.8 : spec.trapRate;
    const SPIKE = spec.spikeRate === undefined ? 0.4 : spec.spikeRate;
    const CHEST = spec.chestRate === undefined ? 0.3 : spec.chestRate;
    const rank = spec.rank || 3;
    for (const r of rooms) {
      const fy = r.y + r.h - 3;                              // 바닥 바로 위 줄
      const cx = r.x + (r.w >> 1);
      // 양쪽 벽에 깃발을 하나씩 걸어 방 하나하나가 "누가 살았던 자리"로 읽히게 한다 (역할 상관없이 전부 — 함정/상자 자리는 안 건드리는 천장 쪽 줄이라 안전하다) 깃발은 벽에 건다
      // — 사연: docs/code-history.md#h119
      if (r.h > 6) { this.putDecor(r.x + 1, r.y + 4, T.BANNER, 'wall'); this.putDecor(r.x + r.w - 2, r.y + 4, T.BANNER, 'wall'); }
      this.putRuinDecor(spec, r, fy, rng);                   // 그 유적에만 있는 장식
      if (r === boss) {
        // 보스방: 넓게 비우고 둥지를 놓는다.
        this.objects.push({ type: 'lair', boss: spec.boss, ruin: idx,
          x: cx * TS, y: (fy + 1) * TS - 48, w: 40, h: 48 });
        for (let x = r.x + 2; x < r.x + r.w - 2; x += 5) this.putDecor(x, r.y + 2, spec.torch, 'any');
        this.grandLights(r, fy, spec.torch, spec.wall);
        this.objects.push({ type: 'chest', tier: clamp(spec.tier, 1, 4),
          x: (r.x + 3) * TS, y: (fy - 0.2) * TS, w: 30, h: 26, items: null });
        continue;
      }
      const role = roles.get(r);

      if (role === 'vault') {
        /* 보물방 — 등급이 가장 높은 상자를 두되, 지킴이가 붙고 바닥이 온통 함정이다. */
        for (let x = r.x + 2; x < r.x + r.w - 2; x += 4) this.putDecor(x, r.y + 2, spec.torch, 'any');
        // 함정 개수와 지킴이 수가 유적 등급을 그대로 탄다 — 갱도는 2마리, 부패한 둥지는 5마리
        for (let k = 0; k < 1 + Math.round(rank * 0.7); k++) this.putTileTrap(r, fy, rng.pick(spec.traps), rng);
        for (let x = r.x + 2; x < r.x + r.w - 2; x++) if (rng.chance(SPIKE)) this.set(x, fy, T.SPIKE);
        /* 이 유적의 유물은 입구에서 가장 먼 보물방에만 들어간다 — 유적마다 하나뿐이고, 끝까지 들어가 본 사람만 갖는다. */
        const first = r === rest[0];
        const relic = first ? RUIN_RELIC[spec.id] : null;
        // 다른 유적의 위치 지도가 여기 들어간다 — 한 곳을 털면 다음 곳이 열리는 사슬
        const mapFor = first ? Object.keys(RUIN_MAP_IN).find(k => RUIN_MAP_IN[k] === spec.id) : null;
        this.objects.push({
          type: 'chest', tier: clamp(spec.tier + 1, 1, 6),
          x: cx * TS, y: (fy - 0.2) * TS, w: 30, h: 26, items: null,
          relic: relic || undefined,
          ruinmap: mapFor ? 'ruinmap_' + mapFor : undefined,
          bonus: spec.bonus, bonus2: spec.bonus2,
          guard: { t: rng.pick(spec.mobs), n: clamp(1 + Math.round(rank * 0.6), 2, 5) }
        });
        continue;
      }

      if (role === 'sig') {
        this.buildSigRoom(spec, r, fy, cx, idx, rng);
        continue;
      }

      if (role === 'mystic') {
        this.buildMysticRoom(spec, r, fy, cx, rng);
        continue;
      }

      if (role === 'lore') {
        // 비문방 — 함정 없이 조용하다.
        for (let x = r.x + 2; x < r.x + r.w - 2; x += 3) this.putDecor(x, r.y + 2, spec.torch, 'any');
        this.objects.push({ type: 'lorestone', lore: spec.id,
          x: cx * TS, y: (fy + 1) * TS - 34, w: 26, h: 34 });
        continue;
      }

      if (role === 'gauntlet') {
        /* 시련방 — 상자가 아예 없다. */
        for (let x = r.x + 3; x < r.x + r.w - 2; x += 8) this.putDecor(x, r.y + 2, spec.torch, 'any');
        for (let k = 0; k < 2 + Math.round(rank * 0.8); k++) this.putTileTrap(r, fy, rng.pick(spec.traps), rng);
        // 천장에서도 쏜다 — 바닥만 보고 걷지 못하게
        for (let x = r.x + 2; x < r.x + r.w - 2; x += 3)
          if (rng.chance(TRAP * 0.55) && this.get(x, r.y + 1) !== T.AIR) this.set(x, r.y + 1, T.FLAMEVENT);
        continue;
      }

      // 평범한 방 — 흔적(짧은 이야기 조각)을 방마다 하나씩 뿌린다
      const hints = RUIN_HINTS[spec.id];
      if (hints && hintSlot < hints.length && rng.chance(0.55)) {
        this.objects.push({ type: 'lorestone', lore: spec.id, hint: hintSlot,
          x: (r.x + 2) * TS, y: (fy + 1) * TS - 28, w: 22, h: 28 });
        hintSlot++;
      }
      for (let x = r.x + 3; x < r.x + r.w - 2; x += 6) this.putDecor(x, r.y + 2, spec.torch, 'any');
      /* 함정은 **세 번** 굴린다. */
      if (rng.chance(TRAP)) this.putTileTrap(r, fy, rng.pick(spec.traps), rng);
      if (rng.chance(TRAP * 0.8)) this.putTileTrap(r, fy, rng.pick(spec.traps), rng);
      if (rng.chance(TRAP * 0.5)) this.putTileTrap(r, fy, rng.pick(spec.traps), rng);
      if (rng.chance(SPIKE)) {
        const sx = r.x + rng.int(2, Math.max(2, r.w - 5));
        for (let k = 0; k < rng.int(2, 2 + Math.round(rank * 0.5)); k++) this.set(sx + k, fy, T.SPIKE);
      }
      // 상자 — 입구에서 멀수록 잘 나온다 거리 보정은 1을 넘지 않게 묶는다 — 넓은 유적에서 far 가 2를 넘어 상자가 쏟아졌다
      const far = Math.min(1, Math.abs(r.x - entX) / Math.max(8, spec.w >> 1));
      if (rng.chance(CHEST + far * 0.14)) {          // 거리 보정도 절반 아래로
        const small = r.w * r.h < 180;
        this.objects.push({ type: 'chest', tier: clamp(spec.tier - 1 + (small ? 1 : 0), 1, 5),
          x: (cx + rng.int(-2, 2)) * TS, y: (fy - 0.2) * TS, w: 30, h: 26, items: null });
      }
    }
    /* 암호 골방은 방 손질이 다 끝난 **뒤에** 세운다 — 앞에서 세우면 고유 방 연출이나 함정이 껍질을 덮어써서 옆으로 파고 들어갈 틈이 생긴다. */
    if (cipherRoom) this.buildCipherVault(spec, cipherRoom, cipherRoom.y + cipherRoom.h - 3, rng, rooms);

    /* ★ 마지막에 연결을 한 번 더 보장한다. */
    this._ensureConnected(x0, y0, spec.w, spec.h, rooms);
    /* ★ 입구가 있으면 입구를 기준으로 삼아 "들어와서 보스까지 갔다가 나갈 수 있는가"가 그대로 검사가 되게 한다. */
    /* 방마다 두 자리를 본다 — 왼쪽 끝과 한가운데(둥지·상자가 놓이는 자리). */
    const spots = [];
    /* 세 번째 값 0 은 "돌아오는 길 검사는 건너뛴다"는 표시다. */
    for (const r of rooms) spots.push([r.x + 2, r.y + r.h - 3, 0], [r.x + (r.w >> 1), r.y + r.h - 3]);
    if (ex !== null) {
      const ent = this._entranceSpots.slice(), mouth = ent.pop();    // 마지막에 넣은 것이 입구 목
      // 기준점(맨 앞)은 입구 목 — "밖에서 들어가 안쪽 끝까지, 그리고 다시 밖으로"가 된다
      spots.unshift(...(mouth ? [mouth] : []), ...ent,
                    /* 입구가 유적에 내려앉는 자리. */
                    this._entranceLandY !== undefined
                      ? [ex, this._entranceLandY]
                      : [clamp(ex, x0 + 1, x0 + spec.w - 2), y0 + 1]);
    }
    /* 실제 손질은 세계를 다 만든 뒤에 한다 — 유적을 지은 다음에도 큰 동굴·물웅덩이· 심층 갱도가 유적을 가로질러 바닥을 헐어 놓는다. */
    this._walkJobs.push([x0, y0, spec.w, spec.h, spots, spec.floor, spec.traps]);
    return site;
  },

  /** 큰 홀 기둥의 받침돌과 불 — 천장 횃불은 둥근 천장에 걸리지 않아 홀 가운데가 캄캄했다.
      ★ 받침돌(벽 타일 두 칸)이 있어야 한다 — 허공의 횃불은 sweepFloatingDecor 가 걷는다. 지나는 길(fy-3 아래)은 비운다. */
  grandLights(r: Bag, fy: number, torch: number, wall: number) {
    for (const px of r.cols || []) for (const y of [fy - 4, fy - 9]) {
      if (y - 1 <= r.y + 1 || [0, 1].some(k => this.get(px + k, y) !== T.AIR || this.get(px + k, y - 1) !== T.AIR)) continue;
      this.set(px, y, wall); this.set(px + 1, y, wall);
      this.set(px, y - 1, torch);
    }
  },

  /* ---- 숨겨진 유적 3곳 + 심층 봉인실 ---- */
  buildRuins(rng: RNG) { const { SX, SY, WW } = this.dims;
    this.ruins = [];
    this.ruinEvents = [];
    this.ruinSites = [];                                      // 석판 유적도 같이 담는다 (진단·저장용)
    this._walkJobs = [];                                      // 마지막에 돌릴 통행 검사 목록
    // 석판은 가장 넓은 방에 둔다 — 사연: docs/code-history.md#h120
    /* 서리(0)가 가장 순하고 부패지대(2)가 가장 사납다. */
    /* 입구 성격(entryKind)도 난이도 계단을 따라간다 — 서리는 발판형, 가운데는 미로형, 부패지대는 무발판형. */
    /* ★ 나중에 지어지는 쪽이 먼저 지은 쪽의 방을 덮어써서, 겹친 자리의 방이 통째로 사라지거나 벽이 어긋났다. */
    const spots = [
      { x: SX(520 + SHIFT),  y: SY(200), trap: 0.52, spike: 0.24, chest: 0.56, w: 96, h: 52, tier: 2, traps: ['dart', 'crumble'], entryKind: 'foothold' },
      { x: SX(1700 + SHIFT), y: SY(300), trap: 0.72, spike: 0.38, chest: 0.60, w: 92, h: 52, tier: 3, traps: ['dart', 'crumble', 'vent'], entryKind: 'maze' },
      { x: SX(3860 + SHIFT), y: SY(340), trap: 0.90, spike: 0.52, chest: 0.64, w: 88, h: 48, tier: 4, traps: ['dart', 'vent', 'crumble'], entryKind: 'nofoothold' }
    ];
    /* ★ 도면(hook, ㄴ 자)이 격자 열둘 중 다섯만 쓰는 데다 상자가 작아서, 방 목표를 12 로 올려도 9~10 에서 더 못 잘랐다(d1 9 · d3 10). */
    spots.forEach((sp, i) => {
      const cx = sp.x, cy = sp.y, w = sp.w, h = sp.h;
      const x0 = cx - (w >> 1), y0 = cy - (h >> 1);
      /* 석판 유적도 바이옴 유적과 같은 규격을 쓴다 — 도면(겉모양) · 묻힌 입구 · 고유 장식 · 고유 방 · 고유 이벤트 — 사연: docs/code-history.md#h121 */
      const st: Bag = STORY_RUIN[i] || {};
      const spec = {
        id: 'story' + i, n: `${tr('석판 유적')} ` + (i + 1), x: cx, y: y0, w, h, tier: sp.tier,
        wall: st.wall || T.RUINBRICK, floor: T.RUINTILE, bg: 10, torch: T.TORCH,
        entryKind: sp.entryKind, plan: st.plan, arch: st.arch,
        decor: st.decor, sig: st.sig, event: st.event, bonus: st.bonus
      };
      const rooms = this.carveDungeon({
        x0, y0, w, h, wall: spec.wall, floor: T.RUINTILE, bg: 10,
        rng, depth: 5, minW: st.bsp ? st.bsp[1] : 16, minH: st.bsp ? st.bsp[2] : 8,
        target: st.rooms, plan: st.plan, worn: 1, style: st.style
      });
      rooms.sort((a: any, b: any) => (b.grand | 0) - (a.grand | 0) || (b.w * b.h) - (a.w * a.h));   // 큰 홀이 주인 방
      const main = rooms[0], fy0 = main.y + main.h - 3;
      this.grandLights(main, fy0, T.TORCH, spec.wall);
      for (let x = main.x + 3; x < main.x + main.w - 2; x += 8) this.set(x, main.y + 2, T.RUNESTONE);
      this.objects.push({ type: 'tablet', tablet: i, x: (main.x + (main.w >> 1)) * TS, y: (fy0 + 1) * TS - 48, w: 34, h: 48 });
      // 지상에서 내려오는 통로 — 이제 지표 아래에 묻는다(sunken).
      this._ruinCtx = { x0, y0, w, rooms };
      const ex = this.carveRuinEntrance(spec, x0, y0, rng);
      this._ruinCtx = null;
      const entX = ex === null ? cx : ex;
      // 유물이 들어갈 방과 고유 방 — 통로에서 먼 것부터
      const rest = rooms.filter((r: any) => r !== main)
        .sort((a: any, b: any) => Math.abs(b.x - entX) - Math.abs(a.x - entX));
      const far = rest[0], sigRoom = rest[1] || rest[0];
      for (const r of rooms) {
        const fy = r.y + r.h - 3, rcx = r.x + (r.w >> 1);
        for (let x = r.x + 3; x < r.x + r.w - 2; x += 6) this.putDecor(x, r.y + 2, T.TORCH, 'any');
        // 깃발은 벽에 건다 — 벽이 안 닿는 자리면 안 건다(허공에 뜬 깃발이 되지 않게)
      if (r.h > 6) { this.putDecor(r.x + 1, r.y + 4, T.BANNER, 'wall'); this.putDecor(r.x + r.w - 2, r.y + 4, T.BANNER, 'wall'); }
        this.putRuinDecor(spec, r, fy, rng);
        if (r === main) continue;
        if (r === sigRoom && st.sig) { this.buildSigRoom(spec, r, fy, rcx, i, rng); continue; }
        if (rng.chance(sp.trap)) this.putTileTrap(r, fy, rng.pick(sp.traps), rng);
        if (rng.chance(sp.trap * 0.75)) this.putTileTrap(r, fy, rng.pick(sp.traps), rng);
        if (rng.chance(sp.trap * 0.45)) this.putTileTrap(r, fy, rng.pick(sp.traps), rng);
        if (i >= 1 && rng.chance(sp.trap * 0.5)) this.putTileTrap(r, fy, rng.pick(sp.traps), rng);
        if (rng.chance(sp.spike)) for (let k = 0; k < rng.int(2, 3 + i); k++) this.set(r.x + 3 + k, fy, T.SPIKE);
        // 유물 방은 상자가 확률이 아니라 확정이다 — 유물은 유적마다 하나뿐이라 굴리면 안 된다
        if (r === far || rng.chance(sp.chest * 0.45))   // 유물 방만 확정, 나머지는 드물게
          this.objects.push({ type: 'chest', tier: sp.tier + (r.w * r.h < 180 ? 1 : 0),
            x: (rcx + rng.int(-2, 2)) * TS, y: (fy - 0.2) * TS, w: 30, h: 26, items: null,
            relic: r === far ? RUIN_RELIC['story' + i] : undefined,
            bonus: r === far ? st.bonus : undefined });
      }
      // 암호 골방 — 방 손질이 끝난 뒤에 세운다(앞서 세우면 함정이 껍질을 덮어쓴다)
      if (RUIN_CIPHER[spec.id]) {
        const cr = (st.sig === 'sunshaft' ? sigRoom : rest.find((q: any) => q.w >= 14)) || sigRoom;
        if (cr) this.buildCipherVault(spec, cr, cr.y + cr.h - 3, rng, rooms);
      }
      this.storyTheme(i, rooms, main, sigRoom, sp, x0, w);
      this._memEnt = [];
      this.storyMemories(i, rooms, main, sigRoom, far, x0, w, sp.tier);
      // 바닥을 갈아 까는 고유 방이 굴을 메울 수 있으므로 마지막에 연결을 다시 보장한다
      this._ensureConnected(x0, y0, w, h, rooms);
      const wsp = [];
      for (const r of rooms) wsp.push([r.x + 2, r.y + r.h - 3, 0], [r.x + (r.w >> 1), r.y + r.h - 3]);
      if (ex !== null) {
        const ent = this._entranceSpots.slice(), mouth = ent.pop();
        wsp.unshift(...(mouth ? [mouth] : []), ...ent, [clamp(ex, x0 + 1, x0 + w - 2), y0 + 1]);
      }
      this._walkJobs.push([x0, y0, w, h, wsp, T.RUINTILE, sp.traps]);  // 걸어서 닿는지는 마지막에
      this.ruins.push({ id: 'story' + i, x: cx, y: cy, w, h });
      this.ruinSites.push({ id: 'story' + i, n: spec.n, x: cx, y: cy, w, h, rooms, idx: i,
                            ent: (this._entranceRooms || []).slice(), hide: this._memEnt,   // hide — 기억 조각 골방(유적 안으로 센다 · 입구 아님)
                            traps: (sp.traps || ['dart', 'crumble']).slice() });
    });

    /* --- 바이옴 유적 5곳 (스토리와 무관한 탐험 콘텐츠) --- */
    const pick = this.ruinSpec.map((_, i) => i);
    for (let i = pick.length - 1; i > 0; i--) { const j = rng.int(0, i); [pick[i], pick[j]] = [pick[j], pick[i]]; }
    /* 신비한 방 자리 셋은 무게(MYSTIC_W)로 고른다 — 체력을 다 채우는 별빛 웅덩이는 세계 셋에 하나 남짓, 'none' 이면 그 유적엔 없다.
       제 난수(_mystic)라 뒤따르는 유적 생성은 그대로다. 사연: docs/code-history.md#h143 */
    const mrng = new RNG(this.seed + '_mystic');
    pick.slice(0, 3).forEach(ri => { delete this.ruinSpec[ri].mystic; });
    pick.slice(0, 3).forEach(ri => {
      const pool = Object.keys(MYSTIC_W).filter(id => id === 'none' || !pick.slice(0, 3).some(o => this.ruinSpec[o].mystic === id));
      const got = weightedKey(Object.fromEntries(pool.map(id => [id, MYSTIC_W[id]])), () => mrng.next());
      if (got === 'none') delete this.ruinSpec[ri].mystic; else this.ruinSpec[ri].mystic = got;
    });
    pick.slice(3).forEach(ri => { delete this.ruinSpec[ri].mystic; });

    this.ruinSpec.forEach((spec, i) => {
      this.ruinSites.push(this.buildRuinSite(spec, i, rng));
      this.ruins.push({ id: spec.id, x: spec.x, y: spec.y! + (spec.h! >> 1), w: spec.w, h: spec.h });
    });

    // 여명 마을(dawnCity, x 2850~2960) 지하에 둔다 — 사연: docs/code-history.md#h122
    const kx = SX(2800 + SHIFT), ky = SY(350), kw = 56, kh = 26;   // 심층 봉인실 — 여명 마을 지하(서쪽)
    const dx0 = kx - kw / 2;
    this.objects.push({ type: 'seal', x: (dx0 + 1) * TS, y: (ky - 2) * TS, w: 44, h: 66 });
    this.objects.push({ type: 'altar', boss: 'first_keeper', x: kx * TS, y: (ky + kh / 2 - 3) * TS - 48, w: 44, h: 48 });
    this.ruins.push({ id: 'seal', x: kx, y: ky, w: kw, h: kh });
    this.sealRoom = { x: kx, y: ky, w: kw, h: kh, dx: dx0 + 1, dy: ky };

    /* ★ 심층 봉인실로 내려가는 길. */
    const sx0 = clamp(kx - 45, 40, WW - 40);
    const surfY = this.surface[sx0];
    this._entranceLandX = null;
    this._carveEntranceShaft(sx0, surfY + 2, ky + 4, {   // 문턱과 같은 높이에서 끝난다
      w: 26, bg: 10, wall: T.RUINBRICK, floor: T.RUINTILE, torch: T.TORCH,
      entryKind: 'foothold', traps: ['dart', 'crumble', 'gas'],
      lo: sx0 - 22, hi: dx0 - 4                             // 봉인문 서쪽 벽을 넘지 않는다
    }, rng);
    // 지상 표식 — 부러진 기둥 셋.
    for (const dx of [-4, -3, 3, 4]) {          // 목(±1)은 비워 둔다 — 덮으면 못 들어간다
      const gx = sx0 + dx, gy = this.surface[clamp(gx, 0, WW - 1)] - 1;
      for (let k = 0; k < (Math.abs(dx) === 3 ? 3 : 2); k++) this.set(gx, gy - k, T.RUINBRICK);
    }
    /* 방과 통로를 짓는 일은 **세계를 다 만든 뒤로 미룬다**(restoreSealRoom). */
    this.sealRoom.endX = this._entranceLandX === null ? sx0 : this._entranceLandX;
    this.restoreSealRoom();
    /* 내려가는 통로도 다른 유적과 같이 "걸어서 오르내릴 수 있는가"를 본다. */
    const ent = this._entranceSpots.slice(), mouth = ent.pop();
    /* 왼쪽은 통로가 좌우로 꺾이며 내려가는 폭(목 기준 -22)까지 넉넉히 넣는다. */
    const jx0 = sx0 - 24, jw = dx0 - jx0 - 12;
    this._walkJobs.push([jx0, ky - 8, jw, 20,
      [...(mouth ? [mouth] : []), ...ent, [dx0 - 4, ky + 4]], T.RUINTILE,
      ['dart', 'crumble', 'gas']]);
  },

  /** 심층 봉인실의 방·문·복도를 (다시) 짓는다. */
  restoreSealRoom() {
    const s = this.sealRoom;
    if (!s) return;
    const kx = s.x, ky = s.y, kw = s.w, kh = s.h, dx0 = kx - kw / 2;
    for (let x = kx - kw / 2; x <= kx + kw / 2; x++)
      for (let y = ky - kh / 2; y <= ky + kh / 2; y++) {
        const edge = (x <= kx - kw / 2 + 2 || x >= kx + kw / 2 - 2 || y <= ky - kh / 2 + 2 || y >= ky + kh / 2 - 2);
        this.set(x, y, edge ? T.RUINBRICK : T.AIR);
        this.setWall(x, y, 10);
      }
    for (let x = kx - kw / 2 + 3; x < kx + kw / 2 - 2; x++) this.set(x, ky + kh / 2 - 3, T.RUINTILE);
    for (let x = kx - 20; x <= kx + 20; x += 10) this.set(x, ky - kh / 2 + 3, T.RUNESTONE);
    for (let x = kx - 22; x <= kx + 22; x += 6) { this.set(x, ky - 6, T.TORCH); this.set(x, ky + 5, T.TORCH); }
    // 봉인문 (세로 통로) — 열쇠로 연다.
    for (let y = ky - 4; y <= ky + 4; y++) { this.set(dx0 + 1, y, T.SEALSTONE); this.set(dx0 + 2, y, T.SEALSTONE); }
    /* 문 앞 복도와 봉인실 안 계단. */
    const ly = ky + 4, endX = s.endX === undefined ? dx0 - 8 : s.endX;
    for (let x = Math.min(endX, dx0 - 8); x <= dx0; x++) {
      for (let y = ly - 2; y <= ly; y++) { this.set(x, y, T.AIR); this.setWall(x, y, 10); }
      this.set(x, ly + 1, T.RUINTILE);                         // 문턱과 같은 높이의 바닥
    }
    // 안쪽 — 바닥(ky+9)에서 세 칸씩 끊어 문턱(ky+4)까지 오르는 발판 두 줄
    for (let x = dx0 + 3; x <= dx0 + 8; x++) this.set(x, ky + 7, T.PLATFORM);
    for (let x = dx0 + 3; x <= dx0 + 7; x++) this.set(x, ky + 5, T.PLATFORM);
  },

  /** 위치 지도를 세계에 흩뿌린다 — 입구 없는 유적(arch: 'buried')마다 두 군데. */
  buildRuinCaches(rng: any) { const { WW, WH } = this.dims;
    for (const spec of this.ruinSpec) {
      if (spec.arch !== 'buried') continue;
      const mapId = 'ruinmap_' + spec.id;

      // ① 유적 바로 위 — 지표 아래 4~7칸에 묻힌 작은 방(hidden 유적은 지상에 흔적을 안 남긴다 — 다른 유적 · 먼 동굴의 지도로만 찾는다)
      const cx = clamp(spec.x + rng.int(-6, 6), 40, WW - 40);
      if (!spec.hidden) {
      const surf = this.surface[cx];
      const cy = surf + rng.int(4, 7);
      for (let x = cx - 3; x <= cx + 3; x++)
        for (let y = cy - 2; y <= cy + 2; y++) {
          const edge = (x === cx - 3 || x === cx + 3 || y === cy - 2 || y === cy + 2);
          this.set(x, y, edge ? T.RUINBRICK : T.AIR);
          this.setWall(x, y, 10);
        }
      for (let x = cx - 2; x <= cx + 2; x++) this.set(x, cy + 1, T.RUINTILE);
      this.set(cx - 2, cy - 1, T.TORCH);
      this.objects.push({ type: 'chest', tier: 2, ruinmap: mapId,
        x: cx * TS, y: (cy - 0.2) * TS, w: 30, h: 26, items: null });
      // 지상 돌무지 — 파 볼 이유를 만든다
      for (const dx of [-2, -1, 1, 2]) {        // 가운데는 비워 둔다 — 파 내려가는 자리
        const gx = cx + dx, gy = this.surface[clamp(gx, 0, WW - 1)] - 1;
        this.set(gx, gy, T.RUINBRICK);
        if (Math.abs(dx) === 1) this.set(gx, gy - 1, T.RUINBRICK);
      }
      }

      // ② 세계 어딘가의 동굴 — 그 유적에서 멀리 떨어진 자리
      const cav = (this.caverns || []).filter(c => Math.abs(c.cx - spec.x!) > 500);
      if (!cav.length) continue;
      const pick = cav[rng.int(0, cav.length - 1)];
      let px = clamp(pick.cx + rng.int(-6, 6), 40, WW - 40), py = pick.cy;
      for (let k = 0; k < 40 && py < WH - 8; k++) {          // 그 동굴 안에서 바닥을 찾는다
        if (this.get(px, py) === T.AIR && TILE_DEF[this.get(px, py + 1)].solid === 1) break;
        py++;
      }
      if (this.get(px, py) !== T.AIR) continue;
      this.objects.push({ type: 'chest', tier: 3, ruinmap: mapId,
        x: px * TS, y: (py - 0.2) * TS, w: 30, h: 26, items: null });
      this.set(px - 1, py, T.TORCH);
    }
  },

  /** 좌표가 유적 내부인지 */
  /** 석판 유적 셋의 얼굴 — 셋 다 같은 벽돌 · 횃불 · 함정이라 이름만 달랐다. 비문이 말하는 대로 판다. 사연: docs/code-history.md#h166
      0 서리 밑 석실: 서리가 벽을 먹어 들어오고(얼음 벽돌 · 언 바닥 · 눈) 천장에 고드름, 불 대신 서리 글자, 얼음 벽 뒤에 묻힌 보물 칸.
      1 겹친 길: 높은 방은 두 층 — 위로 가로지르는 발판 길이 아래 길과 겹치고, 위 길 끝에 상자.
      2 발 디딜 곳 없는 방: 방 바닥이 가시 고랑으로 꺼지고 무너지는 디딤돌 · 드문 쉼돌만 남는다.
      ★ 제 난수(seed+'_story'+i)만 쓴다 — 본 난수를 뽑으면 뒤 유적 · 동굴이 씨앗마다 바뀐다. 걷는 줄 위(fy-2~fy)는 막지 않는다. */
  storyTheme(i: number, rooms: any[], main: Bag, sigRoom: Bag, sp: Bag, x0: number, w: number) {
    const r = new RNG(this.seed + '_story' + i), wall = (STORY_RUIN[i] || {}).wall || T.RUINBRICK;
    const inRoom = (x: number, y: number) => rooms.some((q: any) => x > q.x && x < q.x + q.w - 1 && y > q.y && y < q.y + q.h - 1);
    const chest = (cx: number, fy: number, tier: number, bonus?: string, hidden?: number) =>
      this.objects.push({ type: 'chest', tier, x: cx * TS, y: (fy - 0.2) * TS, w: 30, h: 26, items: null, bonus, cave: hidden });
    if (i === 0) {
      for (const q of rooms) {
        const fy = q.y + q.h - 3;
        for (let x = q.x - 1; x <= q.x + q.w; x++) for (let y = q.y - 1; y <= q.y + q.h; y++) {   // 서리가 벽을 먹는다
          if (this.get(x, y) === wall && r.chance(0.42)) this.set(x, y, T.ICEBRICK);
        }
        const iceFloor = r.chance(0.5);
        for (let x = q.x + 1; x < q.x + q.w - 1; x++) {
          if (this.get(x, fy + 1) === T.RUINTILE && (iceFloor || r.chance(0.3))) this.set(x, fy + 1, iceFloor ? T.ICE : T.SNOW);
          let y = q.y + 1; while (y < fy - 3 && this.get(x, y) !== T.AIR) y++;   // 천장 바로 밑 첫 빈 칸
          if (y < fy - 3 && this.solid(x, y - 1) && r.chance(0.2))
            for (let k = 0, n = r.int(1, 2); k < n && y + k < fy - 2 && this.get(x, y + k) === T.AIR; k++) this.set(x, y + k, T.ICICLE);
          for (let yy = q.y + 1; yy < fy; yy++) if (this.get(x, yy) === T.TORCH && r.chance(0.6)) this.set(x, yy, T.FROSTGLYPH);
        }
      }
      // 얼음 벽 뒤 보물 칸 — 방 옆 바위를 파고 벽을 얼음으로 봉한다. 벽에 서리 글자 하나가 자리를 일러 준다
      for (const q of r.shuffle(rooms.filter(q => q !== main && q !== sigRoom))) {
        const fy = q.y + q.h - 3, side = r.chance(0.5) ? -1 : 1;
        const wx = side < 0 ? q.x : q.x + q.w - 1, nx0 = side < 0 ? q.x - 5 : q.x + q.w + 1;
        let ok = nx0 > x0 + 1 && nx0 + 4 < x0 + w - 1;
        for (let x = nx0 - 1; ok && x <= nx0 + 4; x++) for (let y = fy - 3; ok && y <= fy + 1; y++)
          if (!this.solid(x, y) || inRoom(x, y) || this.get(x, y) === T.BEDROCK) ok = false;
        if (!ok) continue;
        for (let x = nx0; x < nx0 + 4; x++) { for (let y = fy - 2; y <= fy; y++) { this.set(x, y, T.AIR); this.setWall(x, y, 10); } this.set(x, fy + 1, T.ICE); }
        for (const x of [wx, wx + side]) for (let y = fy - 2; y <= fy; y++) this.set(x, y, T.ICE);   // 얼음(단단함 1) — 첫 곡괭이로 깬다
        this.set(wx - side, fy - 4, T.FROSTGLYPH);
        chest(nx0 + 2, fy, sp.tier + 1, 'ice_shard', 1);   // 숨은 덤 — 탐사 100% 의 상자 수에는 안 든다(cave)
        break;
      }
    } else if (i === 1) {
      let gave = false;
      for (const q of rooms) {
        if (q === main || q === sigRoom || q.h < 9 || q.w < 12) continue;
        const fy = q.y + q.h - 3, P = fy - 2;        // 발판 줄 — 머리 위 한 칸, 그 위에 서면 발이 fy-3(점프 3칸 · 실제 4.4칸)
        let built = 0;
        for (let x = q.x + 2; x < q.x + q.w - 2; x++) {
          if ((x - q.x) % 7 === 5 || this.get(x, P) !== T.AIR || this.get(x, P - 1) !== T.AIR) continue;   // 일곱 칸마다 틈
          this.set(x, P, T.PLATFORM); built++;
        }
        if (built < 6) continue;
        if (!gave && r.chance(0.7)) { chest(q.x + (q.w >> 1) + (r.chance(0.5) ? 2 : -2), P - 1, sp.tier); gave = true; }
        if (r.chance(0.5)) { const dx = r.chance(0.5) ? q.x + 1 : q.x + q.w - 2; if (this.solid(dx + (dx === q.x + 1 ? -1 : 1), P - 2)) this.set(dx, P - 2, dx === q.x + 1 ? T.DART_R : T.DART_L); }
      }
    } else {
      for (const q of rooms) {
        if (q === main || q === sigRoom || q.w < 12) continue;
        const fy = q.y + q.h - 3;
        if (!r.chance(0.65)) continue;
        for (let x = q.x + 3; x <= q.x + q.w - 4; x++) {
          if (this.get(x, fy + 1) !== T.RUINTILE && this.get(x, fy + 1) !== T.CRUMBLE) continue;
          if (this.get(x, fy) !== T.AIR) continue;
          const k = x - q.x - 3;
          if (k % 9 === 8) continue;                                  // 쉼돌 — 무너지지 않는다
          if (k % 3 === 1) { this.set(x, fy + 1, T.CRUMBLE); continue; }   // 무너지는 디딤돌
          this.set(x, fy + 1, T.AIR);
          if (this.solid(x, fy + 2)) this.set(x, fy + 2, T.SPIKE);
        }
      }
    }
  },
  /** 석판 유적의 기억 조각 셋 — ① 금 간 벽 뒤 골방(상자 덤) ② 높은 방 천장 턱(발판 사다리로 오른다) ③ 유물 방 구석(줍는 순간 지킴이).
      제 난수(seed+'_mem'+i)만 쓴다. 런타임은 game/memory.ts. 사연: docs/code-history.md#h180 */
  storyMemories(i: number, rooms: any[], main: Bag, sigRoom: Bag, far: Bag, x0: number, w: number, tier: number) {
    const r = new RNG(this.seed + '_mem' + i), id = 'story' + i;
    const inRoom = (x: number, y: number) => rooms.some((q: any) => x >= q.x && x <= q.x + q.w - 1 && y >= q.y && y <= q.y + q.h - 1);
    const shard = (k: number, tx: number, footY: number, guard?: number) =>
      this.objects.push({ type: 'memory', ruin: id, k, guard, x: tx * TS + 1, y: (footY + 1) * TS - 24, w: 20, h: 24 });
    const free = rooms.filter((q: any) => q !== main && q !== sigRoom && q !== far);
    // ① 금 간 벽 뒤 — 방 옆 바위에 4×3 골방을 파고, 방 벽 두 겹을 금 간 벽돌로 바꾼다(곡괭이 첫 등급으로 깬다)
    const pocket = (q: Bag, side: number, fy: number) => {
      const wx = side < 0 ? q.x : q.x + q.w - 1, nx0 = side < 0 ? q.x - 5 : q.x + q.w + 1;
      if (nx0 <= x0 + 1 || nx0 + 4 >= x0 + w - 1) return false;
      for (let x = nx0 - 1; x <= nx0 + 4; x++) for (let y = fy - 3; y <= fy + 1; y++)
        if (!this.solid(x, y) || inRoom(x, y) || this.get(x, y) === T.BEDROCK || this.locked(x, y)) return false;
      for (let y = fy - 2; y <= fy; y++) if (this.get(wx - side, y) !== T.AIR) return false;   // 방 안에서 벽이 보여야 한다
      if (!this.solid(wx - side, fy + 1)) return false;
      for (let x = nx0; x < nx0 + 4; x++) for (let y = fy - 2; y <= fy; y++) { this.set(x, y, T.AIR); this.setWall(x, y, 10); }
      for (const x of [wx, wx + side]) for (let y = fy - 2; y <= fy; y++) this.set(x, y, T.CRACKBRICK);
      shard(0, nx0 + (side < 0 ? 0 : 3), fy);
      this._memEnt.push([nx0, fy - 2, 4, 3]);
      this.objects.push({ type: 'chest', tier: Math.min(6, tier + 1), cave: 1, x: (nx0 + (side < 0 ? 2 : 0)) * TS, y: (fy - 0.2) * TS, w: 30, h: 26, items: null });
      return true;
    };
    /* 옆이 막혔으면(미로처럼 방이 빽빽하면) 바닥 밑 — 바닥 두 칸을 금 간 벽돌로, 그 밑 4×3 골방(떨어져 들어가 뛰어 나온다) */
    const under = (q: Bag) => {
      const fy = q.y + q.h - 3;
      for (let cx = q.x + 3; cx < q.x + q.w - 5; cx++) {
        let ok = this.get(cx, fy) === T.AIR && this.get(cx + 1, fy) === T.AIR;
        for (let x = cx - 2; ok && x <= cx + 3; x++) for (let y = fy + 1; y <= fy + 6; y++)
          if (!this.solid(x, y) || (y > fy + 2 && inRoom(x, y)) || this.get(x, y) === T.BEDROCK || this.locked(x, y) || this.get(x, y) === T.SPIKE) { ok = false; break; }
        if (!ok) continue;
        for (const x of [cx, cx + 1]) for (const y of [fy + 1, fy + 2]) this.set(x, y, T.CRACKBRICK);
        for (let x = cx - 1; x <= cx + 2; x++) for (let y = fy + 3; y <= fy + 5; y++) { this.set(x, y, T.AIR); this.setWall(x, y, 10); }
        shard(0, cx - 1, fy + 5);
        this._memEnt.push([cx - 1, fy + 3, 4, 3]);
        this.objects.push({ type: 'chest', tier: Math.min(6, tier + 1), cave: 1, x: (cx + 1) * TS, y: (fy + 4.8) * TS, w: 30, h: 26, items: null });
        return true;
      }
      return false;
    };
    const cand = [...r.shuffle(free.slice()), sigRoom, main].filter(Boolean);
    let hid = false;
    found: for (const q of cand) for (const side of r.chance(0.5) ? [-1, 1] : [1, -1])
      for (const fy of [q.y + q.h - 3, q.y + q.h - 4, q.y + q.h - 5]) if (pocket(q, side, fy)) { hid = true; break found; }
    /* 그래도 못 팠으면 방 구석에 금 간 벽돌로 쌓은 벽장(2×3) — 방 안이라 늘 놓인다 */
    const closet = (q: Bag) => {
      const fy = q.y + q.h - 3;
      for (const left of [true, false]) {
        const cx0 = left ? q.x + 1 : q.x + q.w - 3, wx = left ? cx0 + 2 : cx0 - 1;
        let ok = this.solid(cx0, fy + 1) && this.solid(cx0 + 1, fy + 1);
        for (let x = Math.min(cx0, wx); ok && x <= Math.max(cx0 + 1, wx); x++) for (let y = fy - 3; y <= fy; y++) if (this.get(x, y) !== T.AIR) { ok = false; break; }
        if (!ok || this.get(wx + (left ? 1 : -1), fy) !== T.AIR) continue;
        for (let y = fy - 2; y <= fy; y++) this.set(wx, y, T.CRACKBRICK);
        for (let x = Math.min(cx0, wx); x <= Math.max(cx0 + 1, wx); x++) this.set(x, fy - 3, T.CRACKBRICK);
        shard(0, left ? cx0 : cx0 + 1, fy);
        return true;
      }
      return false;
    };
    if (!hid) hid = cand.some((q: any) => under(q));
    if (!hid) cand.some((q: any) => closet(q));
    // ② 천장 턱 — 높은 방 한쪽 벽 가까이 3칸 턱, 그 밑으로 세 줄마다 엇갈린 발판(점프 3칸으로 오른다)
    const ledge = (q: Bag) => {
      const fy = q.y + q.h - 3;
      for (const left of r.chance(0.5) ? [true, false] : [false, true])
        for (let ly = q.y + 2; ly <= q.y + 5; ly++) for (let off = 1; off <= 3; off++) {
          const lx0 = left ? q.x + off : q.x + q.w - 3 - off;
          if (fy - ly < 6) continue;
          let ok = true;   // 횃불 · 깃발 같은 안 막는 장식은 걷고 턱을 놓는다
          for (let x = lx0; x < lx0 + 3 && ok; x++) for (let y = ly - 2; y <= ly; y++) if (TILE_DEF[this.get(x, y)].solid === 1 || TILE_DEF[this.get(x, y)].liquid) { ok = false; break; }
          if (!ok) continue;
          for (let x = lx0; x < lx0 + 3; x++) for (let y = ly - 2; y <= ly; y++) if (this.get(x, y) !== T.AIR) this.set(x, y, T.AIR);
          for (let x = lx0; x < lx0 + 3; x++) this.set(x, ly, T.PLATFORM);
          let alt = 0, top = ly;
          for (let y = ly + 3; y < fy - 1; y += 3, alt ^= 1) {
            const sx = left ? lx0 + 3 + alt * 3 : lx0 - 2 - alt * 3;
            let n = 0;
            for (let x = sx; x < sx + 2; x++) if (this.get(x, y) === T.AIR && this.get(x, y - 1) === T.AIR) { this.set(x, y, T.PLATFORM); n++; }
            if (n) top = y;
          }
          if (top === ly) continue;                       // 사다리가 하나도 못 놓였다
          shard(1, lx0 + 1, ly - 1);
          return true;
        }
      return false;
    };
    for (const q of [...free.filter((q: any) => q.h >= 10).sort((a: any, b: any) => b.h - a.h), main]) if (ledge(q)) break;
    // ③ 유물 방 구석 — 상자 반대쪽부터 바닥이 단단한 빈 칸을 찾는다. 집어 들면 지킴이 둘
    const ch = far && this.objects.find((o: Bag) => o.relic && Math.abs(o.x / TS - (far.x + (far.w >> 1))) < far.w);
    const fromLeft = ch ? ch.x / TS > far.x + (far.w >> 1) : r.chance(0.5);
    for (const q of [far, ...free].filter(Boolean)) {   // 유물 방 바닥이 다 가시 고랑이면 다른 방으로
      const fy = q.y + q.h - 3;
      let done = false;
      for (let k = 2; k < q.w - 2 && !done; k++) {
        const tx = fromLeft ? q.x + k : q.x + q.w - 1 - k;
        if (this.get(tx, fy) === T.AIR && this.get(tx, fy - 1) === T.AIR && this.solid(tx, fy + 1) && this.get(tx, fy + 1) !== T.SPIKE && this.get(tx, fy + 1) !== T.CRUMBLE) { shard(2, tx, fy, 1); done = true; }
      }
      if (done) break;
    }
  },
  /** 뒷공사(통행 보수 · 방 잇기)가 바닥을 헐고 간 조각을 아래 첫 바닥으로 내린다 — 공중에 뜬 조각을 남기지 않게 */
  settleMemories() {
    for (const o of this.objects) {
      if (o.type !== 'memory') continue;
      const tx = Math.floor((o.x + o.w / 2) / TS);
      let ty = Math.floor((o.y + o.h - 1) / TS);
      for (let k = 0; k < 14 && !this.solid(tx, ty + 1) && this.get(tx, ty + 1) !== T.PLATFORM; k++) ty++;
      o.y = (ty + 1) * TS - o.h;
    }
  },
  inRuin(tx: number, ty: number) {
    return !!this.ruinInside(tx, ty);
  },
  /** 플레이어가 **유적 안**에 있는가 — 방(벽 한 칸 포함) · 입구의 층계참 · 계단실만. 둘레 상자(ruinAt)로 재면 상자를 지나는
      자연 굴 · 바위를 판 굴까지 유적이 되어 맥박 · 유적 몹 · 입장 카드가 엉뚱한 데서 떴다. 방 기록이 없는 곳(봉인실 · 옛 세이브)은 상자로. 사연: docs/code-history.md#h165 */
  ruinInside(tx: number, ty: number) {
    if (!this.ruins) return null;
    for (const r of this.ruins) {
      const site = this.ruinSites && this.ruinSites.find((s: Bag) => s.id === r.id && s.rooms && s.rooms.length);
      if (!site) { if (tx > r.x - r.w / 2 && tx < r.x + r.w / 2 && ty > r.y - r.h / 2 && ty < r.y + r.h / 2) return r; continue; }
      if (Math.abs(tx - site.x) > site.w / 2 + 60 || Math.abs(ty - site.y) > site.h / 2 + 60) continue;
      for (const m of site.rooms) if (tx >= m.x - 1 && tx <= m.x + m.w && ty >= m.y - 1 && ty <= m.y + m.h) return r;
      for (const e of site.ent || []) if (tx >= e[0] && tx < e[0] + e[2] && ty >= e[1] && ty < e[1] + e[3]) return r;
      for (const e of site.hide || []) if (tx >= e[0] && tx < e[0] + e[2] && ty >= e[1] && ty < e[1] + e[3]) return r;
    }
    return null;
  },
  /** 이 좌표가 속한 유적 자체를 돌려준다 (id 가 붙어 있으면 어느 유적인지도 안다). */
  ruinAt(tx: number, ty: number) {
    if (!this.ruins) return null;
    for (const r of this.ruins)
      if (tx > r.x - r.w / 2 && tx < r.x + r.w / 2 && ty > r.y - r.h / 2 && ty < r.y + r.h / 2) return r;
    return null;
  },
  /** 이 좌표가 속한 바이옴 유적의 잡몹 배율. */
  ruinMobMul(tx: number, ty: number) {
    for (const spec of this.ruinSpec) {
      const hw = spec.w! / 2, y0 = spec.y, y1 = spec.y! + spec.h!;
      if (tx > spec.x! - hw && tx < spec.x! + hw && ty > y0! - 2 && ty < y1 + 2)
        return spec.mobMul === undefined ? 1 : spec.mobMul;
    }
    return 1;
  },

  buildAltars(rng: any) { const { SX, WORLD_BOT } = this.dims;
    // 제단 밑면이 바닥 타일 윗면에 정확히 닿도록: y = 바닥행*TS - h 부패 제단
    const cx1 = SX(2500 + SHIFT), sy1 = this.surface[cx1];
    this.clearBox(cx1 - 14, sy1 - 14, 28, 14);
    for (let x = cx1 - 14; x < cx1 + 14; x++) { this.set(x, sy1, T.EBONSTONE); this.set(x, sy1 + 1, T.EBONSTONE); }
    this.objects.push({ type: 'altar', boss: 'corrupt_heart', x: cx1 * TS, y: sy1 * TS - 44, w: 40, h: 44 });
    // 서리 왕좌
    const cx2 = SX(210 + SHIFT), sy2 = this.surface[cx2];   // 원래 자리 그대로 (밀린 만큼만 옮겨 간다)
    this.clearBox(cx2 - 16, sy2 - 15, 32, 15);
    for (let x = cx2 - 16; x < cx2 + 16; x++) { this.set(x, sy2, T.BRICK); this.set(x, sy2 + 1, T.BRICK); }
    this.objects.push({ type: 'altar', boss: 'frost_witch', x: cx2 * TS, y: sy2 * TS - 44, w: 40, h: 44 });
    // 슬라임 제단 (마을 근처 언덕) — 베이스캠프(vx0..vx1 = 1000..1100, 여유폭 포함 984..1115)와 겹치지 않도록 서쪽으로 충분히 떨어뜨려 둔다
    const cx3 = SX(800 + SHIFT), sy3 = this.surface[cx3];
    this.clearBox(cx3 - 14, sy3 - 13, 28, 13);
    for (let x = cx3 - 14; x < cx3 + 14; x++) { this.set(x, sy3, T.STONE); this.set(x, sy3 + 1, T.STONE); }
    this.objects.push({ type: 'altar', boss: 'king_slime', x: cx3 * TS, y: sy3 * TS - 44, w: 40, h: 44 });
    // 심연 투기장
    const cx4 = SX(1400 + SHIFT), cy4 = WORLD_BOT - 17;
    this.clearBox(cx4 - 36, cy4 - 22, 72, 22);
    for (let x = cx4 - 36; x < cx4 + 36; x++) { this.set(x, cy4, T.OBSIDIAN); this.set(x, cy4 + 1, T.OBSIDIAN); }
    for (let x = cx4 - 36; x < cx4 + 36; x++) for (let y = cy4 - 22; y < cy4; y++) this.setWall(x, y, 3);
    this.objects.push({ type: 'altar', boss: 'void_king', x: cx4 * TS, y: cy4 * TS - 48, w: 44, h: 48 });
    for (let x = cx4 - 32; x < cx4 + 33; x += 9) this.set(x, cy4 - 20, T.TORCH);
  },

  clearBox(x0: any, y0: any, w: any, h: any) {
    for (let x = x0; x < x0 + w; x++) for (let y = y0; y < y0 + h; y++) {
      if (this.get(x, y) === T.LAVA || this.get(x, y) === T.BEDROCK) continue;
      this.set(x, y, T.AIR);
    }
  },
};
mixin(World.prototype, WorldRuinSite, true);
